import Button from '@mui/material/Button';
import type { FC, MouseEventHandler, ReactNode } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';

interface FormSelectTriggerProps {
  onClick: MouseEventHandler<HTMLButtonElement>;
  startIcon?: ReactNode;
  children: ReactNode;
}

/**
 * Neutral dropdown trigger shared by the post composer's form selects
 * (post type, tags). Borderless with a soft-grey fill and a caret.
 */
export const FormSelectTrigger: FC<FormSelectTriggerProps> = ({ onClick, startIcon, children }) => (
  <Button
    variant="text"
    onClick={onClick}
    startIcon={startIcon}
    endIcon={<MdKeyboardArrowDown size={16} />}
    sx={{
      textTransform: 'none',
      borderRadius: '10px',
      fontFamily: 'Figtree, sans-serif',
      fontSize: 13,
      fontWeight: 600,
      px: '12px',
      height: 34,
      // The theme's MuiButton root forces minHeight 40px; override it so this
      // trigger matches the 34px pin / schedule pills (native buttons that the
      // theme override doesn't touch).
      minHeight: 34,
      whiteSpace: 'nowrap',
      border: 'none',
      // Transparent at rest with a soft-grey hover — matches the feed filter
      // trigger buttons (FilterTriggerButton) so the composer controls and the
      // filter row read as one family.
      bgcolor: 'transparent',
      transition: 'background-color 0.15s ease, color 0.15s ease',
      // The theme colors text-primary buttons with primary.main via
      // `root.&.MuiButton-textPrimary` (specificity 0,2,0), which out-ranks
      // a plain sx `color`. Match that class so the label stays neutral grey.
      '&.MuiButton-textPrimary': { color: '#666' },
      '&:hover': { border: 'none' },
      // Soft-grey hover (#F4F4F4). The theme paints a rose tint via its own
      // `.MuiButton-textPrimary:hover` at (0,3,0) specificity, so win with `&&`.
      '&&.MuiButton-textPrimary:hover': { bgcolor: '#F4F4F4' },
      // MUI gives start/end icons -4px outer margins, which makes the left and
      // right padding look uneven — neutralize so both sides match px.
      '& .MuiButton-startIcon': { ml: 0, mr: '6px' },
      '& .MuiButton-endIcon': { ml: '6px', mr: 0 },
    }}
  >
    {children}
  </Button>
);
